import React, {ChangeEvent, useState} from "react";
import {Checkbox, Input, Textarea} from "../components";
import {useTranslation} from "react-i18next";

interface Props {
  formData: ProductForm;
  setFormData: React.Dispatch<React.SetStateAction<ProductForm>>;
}

const GiftCardRecipientForm: React.FC<Props> = ({formData, setFormData}) => {
  const {t} = useTranslation();
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const handleCheckboxChange = (checked: boolean) => {
    setCheckboxChecked(checked);
    setFormData({
      ...formData,
      "properties[__shopify_send_gift_card_to_recipient]": checked
        ? "checked"
        : "",
    });
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const name = e.target.name; // Use the name attribute directly
    setFormData({
      ...formData,
      [name]: e.target.value,
    });

    if (name === "properties[Message]") {
      setCharCount(e.target.value.length); // Update charCount when message changes
    }
  };

  const [charCount, setCharCount] = useState(0); // Add this line

  return (
    <div>
      <label className="flex items-center">
        <Checkbox
          name="properties[__shopify_send_gift_card_to_recipient]"
          checked={checkboxChecked}
          className="mr-2"
          value={
            checkboxChecked
              ? "checked"
              : formData["properties[__shopify_send_gift_card_to_recipient]"]
          }
          onCheckedChange={handleCheckboxChange}
        />
        <span>{t("recipe_details.gift_card.disclaimer")}</span>
      </label>
      {checkboxChecked && (
        <>
          <hr className="md:border-y border-zinc-200 my-2" />
          <Input
            type="email"
            placeholder={t("recipe_details.gift_card.form.email")}
            name="properties[Recipient email]"
            value={formData["properties[Recipient email]"]}
            onChange={handleInputChange}
            className="w-full mx-0"
            aria-invalid={formData.errors.includes("email")}
            aria-describedby={
              formData.errors.includes("email")
                ? `RecipientForm-email-error`
                : undefined
            }
            required={checkboxChecked}
            title="Please enter a valid email address"
          />
          {formData.errors.includes("email") && (
            <div id={`RecipientForm-email-error`}>Email error message</div>
          )}
          <Input
            type="text"
            placeholder={t("recipe_details.gift_card.form.name")}
            name="properties[Recipient name]"
            maxLength={255}
            className="w-full mx-0"
            value={formData["properties[Recipient name]"]}
            onChange={handleInputChange}
            aria-invalid={formData.errors.includes("name")}
            aria-describedby={
              formData.errors.includes("name")
                ? `RecipientForm-name-error`
                : undefined
            }
          />
          {formData.errors.includes("name") && (
            <div id={`RecipientForm-name-error`}>Name error message</div>
          )}
          <Textarea
            name="properties[Message]"
            maxLength={200}
            placeholder={t("recipe_details.gift_card.form.message")}
            className="w-full mx-0 mb-0"
            value={formData["properties[Message]"]}
            onChange={handleInputChange}
            aria-invalid={formData.errors.includes("message")}
            aria-describedby={
              formData.errors.includes("message")
                ? `RecipientForm-message-error`
                : undefined
            }
          />
          <p className="text-gray-500 text-xs mb-2">
            {t("recipe_details.gift_card.form.message_hint", {
              maxCharacterCount: 200,
              currentCharacterCount: charCount,
            })}
          </p>
          {formData.errors.includes("message") && (
            <div id={`RecipientForm-message-error`}>Message error message</div>
          )}
          <Input
            type="date"
            placeholder={t("recipe_details.gift_card.form.date")}
            name="properties[Send on]"
            min={new Date().toISOString().split("T")[0]}
            max={
              new Date(new Date().setDate(new Date().getDate() + 90))
                .toISOString()
                .split("T")[0]
            }
            className="w-full mx-0 mb-0"
            value={formData["properties[Send on]"]}
            onChange={handleInputChange}
            aria-invalid={formData.errors.includes("send_on")}
            aria-describedby={
              formData.errors.includes("send_on")
                ? `RecipientForm-send_on-error`
                : undefined
            }
          />
          <p className="text-gray-500 text-xs mb-2">
            {t("recipe_details.gift_card.form.date")}
          </p>

          {formData.errors.includes("send_on") && (
            <div id={`RecipientForm-send_on-error`}>Send On error message</div>
          )}
        </>
      )}
    </div>
  );
};

export {GiftCardRecipientForm};
